import React, { useEffect, useState, useRef, useCallback } from "react";
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import Search from "../../../components/common/search/Search";

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText
} from '@mui/material';


// UPDATED: Use the same axios instance pattern as Todo List
// Option A: If axiosInstance exists (recommended)
import axiosInstance from "../../../utils/axios/axiosInstance";


// UPDATED: Use react-toastify like Todo List
import { toast } from 'react-toastify';

// UPDATED: Import loading gif like Todo List
import loadingGif from '../../../assets/images/loading.gif';

const NotesListTab = () => {
  // Use ref to track if component is mounted (same as Todo List)
  const isMountedRef = useRef(true);

  // State declarations (keeping existing structure but adding loading states like Todo List)
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // Filter and Pagination states
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState(''); // NEW: Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(6);

  // View mode state
  const [viewMode, setViewMode] = useState('grid');

  // Color picker state
  const [showCustomColorPicker, setShowCustomColorPicker] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: 1,
    color: '',
    pinned: false
  });

  // Form validation state
  const [validationErrors, setValidationErrors] = useState({});
  const [validationTouched, setValidationTouched] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);

  const categoryOptions = [
    { "id": 1, "name": "Academics" },
    { "id": 2, "name": "Motivation" },
    { "id": 3, "name": "Leisure" },
    { "id": 4, "name": "Assignments" },
    { "id": 5, "name": "Exam Preparation" },
    { "id": 6, "name": "Campus Events" },
    { "id": 7, "name": "Clubs & Societies" },
    { "id": 8, "name": "Reminders" },
    { "id": 9, "name": "Personal Notes" },
    { "id": 10, "name": "Goals & Planning" },
    { "id": 11, "name": "Career & Internships" },
    { "id": 12, "name": "Health & Wellness" },
    { "id": 13, "name": "Budget & Finance" },
    { "id": 14, "name": "Study Resources" },
    { "id": 15, "name": "Ideas & Projects" }
  ];

  const colorOptions = [
    { value: "#ffcc00", label: "Yellow" },
    { value: "#ff6b6b", label: "Red" },
    { value: "#4ecdc4", label: "Teal" },
    { value: "#45b7d1", label: "Blue" },
    { value: "#96ceb4", label: "Green" },
    { value: "#ffeaa7", label: "Light Yellow" },
    { value: "#dda0dd", label: "Plum" },
    { value: "#98d8c8", label: "Mint" },
    { value: "#ff9800", label: "Orange" },
    { value: "#9c27b0", label: "Purple" },
    { value: "#607d8b", label: "Blue Grey" },
    { value: "#795548", label: "Brown" }
  ];

  // Color utility functions (keeping existing ones)
  const getLuminance = useCallback((hexColor) => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    const rsRGB = r / 255;
    const gsRGB = g / 255;
    const bsRGB = b / 255;

    const rLinear = rsRGB <= 0.03928 ? rsRGB / 12.92 : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
    const gLinear = gsRGB <= 0.03928 ? gsRGB / 12.92 : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
    const bLinear = bsRGB <= 0.03928 ? bsRGB / 12.92 : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
  }, []);

  const getContrastRatio = useCallback((color1, color2) => {
    const lum1 = getLuminance(color1);
    const lum2 = getLuminance(color2);
    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);
    return (brightest + 0.05) / (darkest + 0.05);
  }, [getLuminance]);

  const getOptimalTextColor = useCallback((backgroundColor, targetContrast = 4.5) => {
    const backgroundLuminance = getLuminance(backgroundColor);
    
    const textColorOptions = [
      { color: '#000000', luminance: 0 },
      { color: '#111827', luminance: 0.0139 },
      { color: '#1f2937', luminance: 0.0284 },
      { color: '#374151', luminance: 0.0591 },
      { color: '#4b5563', luminance: 0.1037 },
      { color: '#6b7280', luminance: 0.1586 },
      { color: '#9ca3af', luminance: 0.2651 },
      { color: '#d1d5db', luminance: 0.4406 },
      { color: '#e5e7eb', luminance: 0.5527 },
      { color: '#f3f4f6', luminance: 0.6883 },
      { color: '#f9fafb', luminance: 0.7549 },
      { color: '#ffffff', luminance: 1 }
    ];

    let bestColor = textColorOptions[0];
    let bestContrast = 0;

    for (const option of textColorOptions) {
      const contrast = getContrastRatio(backgroundColor, option.color);
      if (contrast >= targetContrast && contrast > bestContrast) {
        bestColor = option;
        bestContrast = contrast;
      }
    }

    if (bestContrast < targetContrast) {
      bestColor = textColorOptions.reduce((prev, current) => {
        const prevContrast = getContrastRatio(backgroundColor, prev.color);
        const currentContrast = getContrastRatio(backgroundColor, current.color);
        return currentContrast > prevContrast ? current : prev;
      });
    }

    return bestColor.color;
  }, [getLuminance, getContrastRatio]);

  const isLightColor = useCallback((hexColor) => {
    const luminance = getLuminance(hexColor);
    return luminance > 0.179;
  }, [getLuminance]);

  const getTextColors = useCallback((backgroundColor) => {
    const backgroundLuminance = getLuminance(backgroundColor);
    const isVeryLight = backgroundLuminance > 0.8;
    const isVeryDark = backgroundLuminance < 0.1;
    
    const primaryText = getOptimalTextColor(backgroundColor, 7.0);
    const secondaryText = getOptimalTextColor(backgroundColor, 4.5);
    const mutedText = getOptimalTextColor(backgroundColor, 3.0);
    
    const colorPalette = {
      primary: primaryText,
      secondary: secondaryText,
      muted: mutedText,
      accent: isVeryLight ? '#1565c0' : isVeryDark ? '#64b5f6' : '#1976d2',
      success: isVeryLight ? '#2e7d32' : isVeryDark ? '#66bb6a' : '#4caf50',
      warning: isVeryLight ? '#f57c00' : isVeryDark ? '#ffb74d' : '#ff9800',
      error: isVeryLight ? '#d32f2f' : isVeryDark ? '#f48fb1' : '#f44336',
      button: primaryText,
      buttonHover: backgroundColor,
      categoryBg: isVeryLight 
        ? 'rgba(0, 0, 0, 0.08)' 
        : isVeryDark 
          ? 'rgba(255, 255, 255, 0.15)' 
          : 'rgba(0, 0, 0, 0.05)',
      categoryText: secondaryText,
      border: isVeryLight 
        ? 'rgba(0, 0, 0, 0.12)' 
        : isVeryDark 
          ? 'rgba(255, 255, 255, 0.12)' 
          : 'rgba(0, 0, 0, 0.08)',
      shadow: isVeryLight 
        ? 'rgba(0, 0, 0, 0.15)' 
        : isVeryDark 
          ? 'rgba(0, 0, 0, 0.5)' 
          : 'rgba(0, 0, 0, 0.1)',
      link: isVeryLight ? '#1565c0' : isVeryDark ? '#64b5f6' : '#1976d2',
      linkHover: isVeryLight ? '#0d47a1' : isVeryDark ? '#90caf9' : '#1565c0',
      focus: isVeryLight ? '#1976d2' : isVeryDark ? '#64b5f6' : '#2196f3',
      selection: isVeryLight ? 'rgba(25, 118, 210, 0.2)' : 'rgba(100, 181, 246, 0.2)',
    };

    return colorPalette;
  }, [getLuminance, getOptimalTextColor]);

  const validateContrast = useCallback((backgroundColor, textColor) => {
    const contrast = getContrastRatio(backgroundColor, textColor);
    return {
      ratio: contrast,
      AA: contrast >= 4.5,
      AAA: contrast >= 7.0,
      level: contrast >= 7.0 ? 'AAA' : contrast >= 4.5 ? 'AA' : 'Fail'
    };
  }, [getContrastRatio]);

  // UPDATED: Form validation functions following Todo List pattern
  const validateTitle = useCallback((title) => {
    const trimmedTitle = title?.trim() || '';

    if (!trimmedTitle) {
      return 'Note title is required';
    }
    if (trimmedTitle.length < 3) {
      return 'Title must be at least 3 characters long';
    }
    if (trimmedTitle.length > 200) {
      return 'Title must be less than 200 characters';
    }
    return null;
  }, []);

  const validateDescription = useCallback((description) => {
    const trimmedDesc = description?.trim() || '';

    if (!trimmedDesc) {
      return 'Description is required';
    }
    if (trimmedDesc.length < 3) {
      return 'Description must be at least 3 characters long';
    }
    if (trimmedDesc.length > 1000) {
      return 'Description must be less than 1000 characters';
    }
    return null;
  }, []);

  const validateCategory = useCallback((categoryId) => {
    if (!categoryId) {
      return 'Category is required';
    }
    
    const numericId = parseInt(categoryId);
    if (isNaN(numericId) || numericId < 1) {
      return 'Please select a valid category';
    }
    
    if (categoryOptions.length > 0 && !categoryOptions.find(cat => cat.id === numericId)) {
      return 'Selected category does not exist';
    }
    
    return null;
  }, []);

  // NEW: Color validation function
  const validateColor = useCallback((color) => {
    const trimmedColor = color?.trim() || '';

    if (!trimmedColor) {
      return 'Color is required';
    }

    // Validate hex color format
    const hexColorRegex = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    if (!hexColorRegex.test(trimmedColor)) {
      return 'Please enter a valid hex color (e.g., #ff0000 or #f00)';
    }

    // Optional: Validate contrast for accessibility
    try {
      const textColors = getTextColors(trimmedColor);
      const contrastInfo = validateContrast(trimmedColor, textColors.primary);
      
      if (contrastInfo.ratio < 3.0) {
        return 'Color may have poor readability. Consider choosing a different color.';
      }
    } catch (err) {
      // If contrast validation fails, just continue
      console.warn('Color contrast validation failed:', err);
    }

    return null;
  }, [getTextColors, validateContrast]);

  const validateForm = useCallback((data = formData) => {
    const errors = {};

    const titleError = validateTitle(data.name);
    if (titleError) errors.name = titleError;

    const descriptionError = validateDescription(data.description);
    if (descriptionError) errors.description = descriptionError;

    const categoryError = validateCategory(data.category);
    if (categoryError) errors.category = categoryError;

    // UPDATED: Add color validation
    const colorError = validateColor(data.color);
    if (colorError) errors.color = colorError;

    setValidationErrors(errors);
    const isValid = Object.keys(errors).length === 0;
    setIsFormValid(isValid);

    return { isValid, errors };
  }, [formData, validateTitle, validateDescription, validateCategory, validateColor]);

  const validateField = useCallback((fieldName, value) => {
    let error = null;

    switch (fieldName) {
      case 'name':
        error = validateTitle(value);
        break;
      case 'description':
        error = validateDescription(value);
        break;
      case 'category':
        error = validateCategory(value);
        break;
      case 'color':
        error = validateColor(value);
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));

    return error;
  }, [validateTitle, validateDescription, validateCategory, validateColor]);

  const markFieldAsTouched = useCallback((fieldName) => {
    setValidationTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  }, []);

  const resetForm = useCallback(() => {
    setFormData({
      name: '',
      description: '',
      category: 1,
      color: '',
      pinned: false
    });
    setValidationErrors({});
    setValidationTouched({});
    setIsFormValid(false);
    setEditMode(false);
    setEditingNote(null);
    setShowCustomColorPicker(false);
  }, []);

  const handleInputChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field !== 'pinned') {
      markFieldAsTouched(field);
      validateField(field, value);
    }
  }, [markFieldAsTouched, validateField]);

  const handleColorSelect = useCallback((color) => {
    handleInputChange('color', color);
    setShowCustomColorPicker(false);
  }, [handleInputChange]);

  const handleCustomColorChange = useCallback((e) => {
    const color = e.target.value;
    handleInputChange('color', color);
  }, [handleInputChange]);

  const isCustomColor = useCallback((color) => {
    return !colorOptions.find(option => option.value.toLowerCase() === color.toLowerCase());
  }, []);

  const getColorName = useCallback((color) => {
    const colorOption = colorOptions.find(option => option.value.toLowerCase() === color.toLowerCase());
    return colorOption ? colorOption.label : 'Custom Color';
  }, []);

  useEffect(() => {
    if (Object.keys(validationTouched).length > 0) {
      validateForm();
    }
  }, [formData, validateForm, validationTouched]);

  // UPDATED: API FUNCTIONS following Todo List pattern

  // Fetch notes from API (following Todo List pattern)
  const fetchNotes = useCallback(async () => {
    if (!isMountedRef.current) return;

    try {
      setLoading(true);
      setError(null);

      console.log('📡 Fetching notes from API...');
      
      const response = await axiosInstance.get(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/notes`);
      
      if (!isMountedRef.current) return;

      console.log('📥 API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const notesData = response.data.data || [];
        
        // Transform API data to match our component structure if needed
        const transformedNotes = notesData.map(note => ({
          id: note.id,
          name: note.name || note.title,
          description: note.description,
          category: parseInt(note.category) || 1,
          color: note.color || '#ffcc00',
          pinned: Boolean(note.pinned),
          created_at: note.created_at || note.createdAt || new Date().toISOString(),
          updated_at: note.updated_at || note.updatedAt || new Date().toISOString()
        }));

        setNotes(transformedNotes);
        
        if (transformedNotes.length === 0) {
          toast.info('No notes found. Create your first note! 📝', {
            position: "top-center",
            autoClose: 3000,
          });
        } else {
          toast.success(`Loaded ${transformedNotes.length} notes successfully! 📝`, {
            position: "top-center",
            autoClose: 3000,
          });
        }
      } else {
        throw new Error(response.data?.message || 'Failed to fetch notes');
      }
    } catch (err) {
      console.error('❌ Error fetching notes:', err);
      
      let errorMessage = 'Failed to load notes';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, []);

  // Create new note (following Todo List pattern)
  const createNote = useCallback(async () => {
    if (!isMountedRef.current) return;

    // UPDATED: Mark required fields as touched for validation display (including color)
    const requiredFields = ['name', 'description', 'category', 'color'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    const { isValid, errors } = validateForm();

    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const noteData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: String(formData.category), 
        color: formData.color,
        pinned: formData.pinned
      };

      console.log('📤 Creating note:', noteData);

      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/notes/create`, noteData);

      if (!isMountedRef.current) return;

      console.log('📥 Create API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const newNote = response.data.data;
        
        // Transform API response to match our component structure
        const transformedNote = {
          id: newNote.id,
          name: newNote.name || newNote.title,
          description: newNote.description,
          category: newNote.category || 1,
          color: newNote.color || '#ffcc00',
          pinned: Boolean(newNote.pinned),
          created_at: newNote.created_at || newNote.createdAt || new Date().toISOString(),
          updated_at: newNote.updated_at || newNote.updatedAt || new Date().toISOString()
        };

        setNotes(prev => [transformedNote, ...prev]);
        setOpen(false);
        resetForm();
        setError(null);

        toast.success(
          formData.pinned ? 'Note created and pinned! 🎉📌' : 'Note created successfully! 🎉', {
            position: "top-center",
            autoClose: 3000,
          }
        );
      } else {
        throw new Error(response.data?.message || 'Failed to create note');
      }
    } catch (err) {
      console.error('❌ Error creating note:', err);
      
      let errorMessage = 'Failed to create note';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        
        // Handle validation errors specifically
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errors = err.response.data.errors;
          errorMessage = errors.join(', ');
        }
      } else if (err.response?.data?.errors) {
        if (Array.isArray(err.response.data.errors)) {
          errorMessage = err.response.data.errors.join(', ');
        } else {
          errorMessage = err.response.data.errors;
        }
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [formData, resetForm, markFieldAsTouched, validateForm]);

  // FIXED: Update note function with correct API endpoint and method
  const updateNote = useCallback(async () => {
    if (!isMountedRef.current || !editingNote) return;

    // UPDATED: Mark required fields as touched for validation display (including color)
    const requiredFields = ['name', 'description', 'category', 'color'];
    requiredFields.forEach(field => markFieldAsTouched(field));

    const { isValid, errors } = validateForm();

    if (!isValid) {
      toast.error('Please fix the validation errors before submitting', {
        position: "top-center",
        autoClose: 5000,
      });
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const noteData = {
        id: editingNote.id,
        name: formData.name.trim(),
        description: formData.description.trim(),
        category: String(formData.category),
        color: formData.color,
        pinned: formData.pinned
      };

      console.log('📤 Updating note:', noteData);

      // FIXED: Changed from PUT to POST and updated endpoint
      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/notes/edit`, noteData);

      if (!isMountedRef.current) return;

      console.log('📥 Update API Response:', response.data);

      if (response.data && response.data.status === 200) {
        const updatedNote = response.data.data;
        
        const processedNote = {
          id: updatedNote.id,
          name: updatedNote.name || updatedNote.title,
          description: updatedNote.description,
          category: updatedNote.category || 1,
          color: updatedNote.color || '#ffcc00',
          pinned: Boolean(updatedNote.pinned),
          created_at: updatedNote.created_at || editingNote.created_at,
          updated_at: updatedNote.updated_at || new Date().toISOString()
        };

        setNotes(prev => prev.map(note =>
          note.id === editingNote.id ? processedNote : note
        ));

        setOpen(false);
        resetForm();
        setError(null);

        toast.success('Note updated successfully! ✅', {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to update note');
      }
    } catch (err) {
      console.error('❌ Error updating note:', err);
      
      let errorMessage = 'Failed to update note';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        
        // Handle validation errors specifically
        if (err.response.data.errors && Array.isArray(err.response.data.errors)) {
          const errors = err.response.data.errors;
          errorMessage = errors.join(', ');
        }
      } else if (err.response?.data?.errors) {
        if (Array.isArray(err.response.data.errors)) {
          errorMessage = err.response.data.errors.join(', ');
        } else {
          errorMessage = err.response.data.errors;
        }
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }
      
      if (isMountedRef.current) {
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 5000,
        });
      }
    } finally {
      if (isMountedRef.current) {
        setSubmitting(false);
      }
    }
  }, [formData, editingNote, resetForm, markFieldAsTouched, validateForm]);

  // Delete note (following Todo List pattern)
  const deleteNote = useCallback(async (noteId) => {
    if (!noteId || !isMountedRef.current) return;

    try {
      console.log('📤 Deleting note:', noteId);

      const response = await axiosInstance.delete(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/notes/${noteId}`);

      if (!isMountedRef.current) return;

      console.log('📥 Delete API Response:', response.data);

      if (response.data?.status === 200 || response.status === 200) {
        setNotes(prev => prev.filter(note => note.id !== noteId));
        
        toast.success('Note deleted successfully!', {
          position: "top-center",
          autoClose: 2000,
        });
      } else {
        throw new Error(response.data?.message || 'Failed to delete note');
      }
    } catch (err) {
      console.error('❌ Error deleting note:', err);

      let errorMessage = 'Failed to delete note';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  }, []);

  // FIXED: Toggle pin status with correct API endpoint and method
  const togglePinNote = useCallback(async (noteId, currentPinnedStatus) => {
    if (!noteId || !isMountedRef.current) return;

    try {
      const newPinnedStatus = !currentPinnedStatus;

      console.log('📤 Toggling pin status:', { noteId, newPinnedStatus });

      // FIXED: Changed from PUT to POST and updated endpoint
      const response = await axiosInstance.post(`${process.env.REACT_APP_TOOLS_API_BASE_URL}v1/notes/pin`, { 
        id: noteId,
        pinned: newPinnedStatus 
      });

      if (!isMountedRef.current) return;

      console.log('📥 Pin API Response:', response.data);

      if (response.data?.status === 200 || response.status === 200) {
        setNotes(prev => prev.map(note =>
          note.id === noteId ? { ...note, pinned: newPinnedStatus } : note
        ));

        toast.success(
          newPinnedStatus ? 'Note pinned to top! 📌' : 'Note unpinned! 📝', {
            position: "top-center",
            autoClose: 2000,
          }
        );
      } else {
        throw new Error(response.data?.message || 'Failed to update pin status');
      }
    } catch (err) {
      console.error('❌ Error toggling pin status:', err);
      
      let errorMessage = 'Failed to update pin status';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response) {
        errorMessage = `Server error: ${err.response.status} ${err.response.statusText}`;
      } else if (err.request) {
        errorMessage = 'Network error: Unable to connect to server';
      } else {
        errorMessage = err.message || 'Unknown error occurred';
      }

      if (isMountedRef.current) {
        toast.error(errorMessage, {
          position: "top-center",
          autoClose: 3000,
        });
      }
    }
  }, []);

  // UPDATED: Initialize component following Todo List pattern
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      await fetchNotes();
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [fetchNotes]);

  // Handle form submission
  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    console.log('📝 Form submission:', formData);
    console.log('📝 Edit mode:', editMode);
    console.log('📝 Form validation state:', { isFormValid, validationErrors });

    if (editMode) {
      updateNote();
    } else {
      createNote();
    }
  }, [formData, editMode, createNote, updateNote, isFormValid, validationErrors]);

  // Handle edit note
  const handleEdit = useCallback((note) => {
    setEditMode(true);
    setEditingNote(note);

    const editFormData = {
      name: note.name || '',
      description: note.description || '',
      category: parseInt(note.category) || 1,
      color: note.color || '#ffcc00',
      pinned: Boolean(note.pinned)
    };

    setFormData(editFormData);
    setValidationErrors({});
    setValidationTouched({});
    setShowCustomColorPicker(false);

    setTimeout(() => {
      validateForm(editFormData);
    }, 100);

    setOpen(true);
  }, [validateForm]);

  // Handle search functionality (matching TodoList pattern)
  const handleSearchChange = useCallback((e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when search changes
  }, []);

  // Cleanup effect
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Helper function to get category info
  const getCategoryInfo = useCallback((categoryId) => {
    const category = categoryOptions.find(cat => cat.id === parseInt(categoryId));
    const categoryName = category ? category.name.toLowerCase() : '';
    let color = '#795548';
    let bgColor = '#efebe9';

    switch (categoryName) {
      case 'academics':
        color = '#3f51b5';
        bgColor = '#e8eaf6';
        break;
      case 'motivation':
        color = '#ff9800';
        bgColor = '#fff3e0';
        break;
      case 'leisure':
        color = '#4caf50';
        bgColor = '#e8f5e9';
        break;
      case 'assignments':
        color = '#f44336';
        bgColor = '#ffebee';
        break;
      case 'exam preparation':
        color = '#9c27b0';
        bgColor = '#f3e5f5';
        break;
      case 'campus events':
        color = '#2196f3';
        bgColor = '#e3f2fd';
        break;
      case 'clubs & societies':
        color = '#673ab7';
        bgColor = '#ede7f6';
        break;
      case 'reminders':
        color = '#ff5722';
        bgColor = '#fbe9e7';
        break;
      case 'personal notes':
        color = '#607d8b';
        bgColor = '#eceff1';
        break;
      case 'goals & planning':
        color = '#009688';
        bgColor = '#e0f2f1';
        break;
      case 'career & internships':
        color = '#795548';
        bgColor = '#efebe9';
        break;
      case 'health & wellness':
        color = '#8bc34a';
        bgColor = '#f1f8e9';
        break;
      case 'budget & finance':
        color = '#4caf50';
        bgColor = '#e8f5e9';
        break;
      case 'study resources':
        color = '#3f51b5';
        bgColor = '#e8eaf6';
        break;
      case 'ideas & projects':
        color = '#ff9800';
        bgColor = '#fff3e0';
        break;
      default:
        break;
    }

    return {
      label: category ? category.name : 'Unknown Category',
      color,
      bgColor
    };
  }, []);

  const handleAddNew = useCallback(() => {
    setEditMode(false);
    setEditingNote(null);
    resetForm();
    setOpen(true);
  }, [resetForm]);

  const closeSlideWindow = useCallback(() => {
    setOpen(false);
    resetForm();
    setError(null);
  }, [resetForm]);

  // UPDATED: Filter and sort notes with search functionality
  const getFilteredAndSortedNotes = useCallback(() => {
    let filteredNotes = [...notes];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filteredNotes = filteredNotes.filter(note => 
        note.name?.toLowerCase().includes(query) || 
        note.description?.toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (categoryFilter !== 'all') {
      const categoryId = parseInt(categoryFilter);
      filteredNotes = filteredNotes.filter(note => parseInt(note.category) === categoryId);
    }

    // Sort notes (pinned first, then by creation date)
    filteredNotes.sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA;
    });

    return filteredNotes;
  }, [notes, categoryFilter, searchQuery]);

  const getPaginatedNotes = useCallback(() => {
    const filteredNotes = getFilteredAndSortedNotes();
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredNotes.slice(startIndex, endIndex);
  }, [getFilteredAndSortedNotes, currentPage, itemsPerPage]);

  const filteredAndSortedNotes = getFilteredAndSortedNotes();
  const totalPages = Math.ceil(filteredAndSortedNotes.length / itemsPerPage);
  const totalItems = filteredAndSortedNotes.length;

  const handlePageChange = useCallback((value) => {
    setCurrentPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const getFieldError = useCallback((fieldName) => {
    return validationTouched[fieldName] && validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  const isFieldValid = useCallback((fieldName) => {
    return validationTouched[fieldName] && !validationErrors[fieldName];
  }, [validationTouched, validationErrors]);

  const truncateText = useCallback((text, maxLength) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }, []);

  // UPDATED: Loading state following Todo List pattern
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ padding: '32px', textAlign: 'center' }}>
          {loadingGif ? (
            <img
              src={loadingGif}
              alt="Loading..."
              style={{ width: '80px', marginBottom: '16px' }}
            />
          ) : (
            <div style={{ marginBottom: '16px', fontSize: '48px' }}>⏳</div>
          )}
          <div style={{ fontSize: '20px', color: '#666', fontFamily: '"Saira", sans-serif' }}>
            Loading your notes...
          </div>
        </div>
      </div>
    );
  }

  // UPDATED: Error state following Todo List pattern
  if (error && !notes.length) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #E3F2FD 0%, #E8EAF6 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{ padding: '32px', maxWidth: '400px', margin: '0 16px', textAlign: 'center' }}>
          <div style={{ fontSize: '48px', color: '#f44336', marginBottom: '16px' }}>❌</div>
          <div style={{ 
            padding: '16px', 
            backgroundColor: '#ffebee', 
            border: '1px solid #f44336',
            borderRadius: '8px',
            marginBottom: '24px',
            fontFamily: '"Saira", sans-serif'
          }}>
            <strong>Error!</strong> {error}
          </div>
          <button
            onClick={() => fetchNotes()}
            style={{
              padding: '12px 24px',
              backgroundColor: '#1976d2',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontFamily: '"Saira", sans-serif'
            }}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const paginatedNotes = getPaginatedNotes();

  return (
    <div style={{ padding: '16px' }}>
      <div className="container">
        <div className="css-11ezll3-MuiToolbar-root breadcrumbs">
          <div className="top-sec">
            <div className="card custom-card">
              <div className="card-body">
                {/* UPDATED: Error display following Todo List pattern */}
                {error && notes.length > 0 && (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#ffebee',
                    border: '1px solid #f44336',
                    borderRadius: '8px',
                    marginBottom: '24px',
                    fontFamily: '"Saira", sans-serif',
                    color: '#c62828'
                  }}>
                    <strong>Error!</strong> {error}
                    <button
                      onClick={() => setError(null)}
                      style={{
                        float: 'right',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '18px',
                        color: '#c62828'
                      }}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/* Header Section */}
                <div className="d-flex align-items-center justify-content-between flex-wrap pb-1">
                  <div className="d-flex align-items-center">
                    <h3 className="mb-2" style={{ fontFamily: '"Saira", sans-serif' }}>
                      Sticky Notes List
                      {loading && <span style={{ marginLeft: '8px', fontSize: '16px' }}>⏳</span>}
                    </h3>
                  </div>
                  <div className="d-flex align-items-center flex-wrap mb-1">
                    <button 
                      onClick={handleAddNew}
                      className="!w-fit !px-3 !h-10"
                      disabled={submitting}
                      style={{
                        backgroundColor: '#ce55b9',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '2px 24px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        fontFamily: '"Saira", sans-serif',
                        opacity: submitting ? 0.6 : 1
                      }}
                    >
                      {submitting ? 'Processing...' : '+ Add New Note'}
                    </button>
                  </div>
                </div>

                {/* UPDATED: Filter Panel with Search Bar */}
                <div className="row filter-panel mt-4">
                  <div className="col-md-4">
                    <div className="serarch-bar">
                      <Search
                        value={searchQuery}
                        handleInputChange={(e) => handleSearchChange(e)}
                      />
                    </div>
                  </div>
                  <div className="col-md-8">
                    <div className="w-full flex items-center justify-end gap-2">
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <div style={{ 
                          border: '1px solid #ddd',
                          borderRadius: '8px',
                          display: 'flex',
                          backgroundColor: 'white'
                        }}>
                          <button
                            onClick={() => setViewMode('grid')}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: viewMode === 'grid' ? '#1976d2' : 'transparent',
                              color: viewMode === 'grid' ? 'white' : '#666',
                              border: 'none',
                              borderRadius: '6px 0 0 6px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            title="Grid View"
                          >
                            ⊞
                          </button>
                          <button
                            onClick={() => setViewMode('list')}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: viewMode === 'list' ? '#1976d2' : 'transparent',
                              color: viewMode === 'list' ? 'white' : '#666',
                              border: 'none',
                              borderRadius: '0 6px 6px 0',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                            title="List View"
                          >
                            ☰
                          </button>
                        </div>

                       <FormControl
  size="small"
  sx={{
    minWidth: '200px',
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#ce55b9',
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#ce55b9'
    }
  }}
>
  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>
    Category
  </InputLabel>
  <Select
    value={categoryFilter}
    label="Category"
    onChange={(e) => setCategoryFilter(e.target.value)}
    sx={{
      fontFamily: '"Saira", sans-serif',
      fontWeight: 500,
      '& .MuiSelect-select': {
        fontFamily: '"Saira", sans-serif'
      }
    }}
  >
    <MenuItem value="all" sx={{ fontFamily: '"Saira", sans-serif' }}>
      All Categories
    </MenuItem>
    {categoryOptions.map((category) => (
      <MenuItem
        key={category.id}
        value={category.id.toString()}
        sx={{ fontFamily: '"Saira", sans-serif' }}
      >
        {category.name}
      </MenuItem>
    ))}
  </Select>
</FormControl>
                      </div>
                    </div>
                  </div>
                </div>

                {/* NEW: Search Results Info */}
                {(searchQuery || categoryFilter !== 'all') && (
                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px 16px', 
                    backgroundColor: '#f5f5f5', 
                    borderRadius: '8px',
                    fontFamily: '"Saira", sans-serif',
                    fontSize: '14px',
                    color: '#666'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
                      <div>
                        Showing {totalItems} result{totalItems !== 1 ? 's' : ''}
                        {searchQuery && <span> for "<strong>{searchQuery}</strong>"</span>}
                        {categoryFilter !== 'all' && (
                          <span> in <strong>{categoryOptions.find(cat => cat.id === parseInt(categoryFilter))?.name}</strong></span>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {searchQuery && (
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setCurrentPage(1);
                            }}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#666',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontFamily: '"Saira", sans-serif'
                            }}
                          >
                            Clear search
                          </button>
                        )}
                        {categoryFilter !== 'all' && (
                          <button
                            onClick={() => {
                              setCategoryFilter('all');
                              setCurrentPage(1);
                            }}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#666',
                              border: '1px solid #ccc',
                              borderRadius: '4px',
                              padding: '4px 8px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontFamily: '"Saira", sans-serif'
                            }}
                          >
                            Clear filter
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Notes Grid/List */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: viewMode === 'grid' ? 'repeat(auto-fill, minmax(350px, 1fr))' : '1fr',
                  gap: '24px', 
                  marginTop: '32px', 
                  marginBottom: '32px' 
                }}>
                  {paginatedNotes.length > 0 ? (
                    paginatedNotes.map((note) => {
                      const categoryInfo = getCategoryInfo(note.category);
                      const textColors = getTextColors(note.color);
                      const isGridView = viewMode === 'grid';
                      const contrastInfo = validateContrast(note.color, textColors.primary);

                      return (
                        <div
                          key={note.id}
                          style={{
                            backgroundColor: note.color,
                            border: note.pinned ? '2px solid #1976d2' : '1px solid #ddd',
                            borderRadius: '12px',
                            padding: isGridView ? '24px' : '32px',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '16px',
                            boxShadow: `0 2px 8px ${textColors.shadow}`,
                            cursor: 'default'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-4px)';
                            e.currentTarget.style.boxShadow = `0 8px 25px ${textColors.shadow}`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = `0 2px 8px ${textColors.shadow}`;
                          }}
                        >
                          {/* Top Section */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div className="pinned-active" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{
                                backgroundColor: textColors.categoryBg,
                                color: textColors.categoryText,
                                padding: '4px 12px',
                                borderRadius: '16px',
                                fontSize: isGridView ? '12px' : '14px',
                                fontWeight: '600',
                                fontFamily: '"Saira", sans-serif'
                              }}>
                                {isGridView ? truncateText(categoryInfo.label, 12) : categoryInfo.label}
                              </span>
                              {note.pinned && (
                                <span 
                                  style={{
                                    fontSize: isGridView ? '32px' : '32px',
                                    color:'#fff',
                                    animation: 'pulse 2s infinite'
                                  }}
                                  title="Pinned Note"
                                >
                                  <PushPinIcon sx={{ fontSize: isGridView ? '32px' : '32px' }} />
                                </span>
                              )}
                            </div>

                            {/* Action Buttons */}
                            <div style={{ 
                              display: 'flex', 
                              gap: '4px', 
                              flexDirection: isGridView ? 'column' : 'row' 
                            }}>
                              <button
                                onClick={() => togglePinNote(note.id, note.pinned)}
                                disabled={submitting || loading}
                                style={{
                                  backgroundColor: '#fff',
                                  color:'#000',
                                  border: 'none',
                                  borderRadius: '6px',
                                  width: '32px',
                                  height: '32px',
                                  cursor: submitting || loading ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  opacity: submitting || loading ? 0.6 : 1
                                }}
                                title={note.pinned ? 'Unpin Note' : 'Pin Note'}
                              >
                                {note.pinned ? 
                                  <PushPinIcon sx={{ fontSize: '16px' }} /> : 
                                  <PushPinOutlinedIcon sx={{ fontSize: '16px' }} />
                                }
                              </button>

                              <button
                                onClick={() => handleEdit(note)}
                                disabled={submitting || loading}
                                style={{
                                  backgroundColor: '#fff',
                                  color: '#000',
                                  border: 'none',
                                  borderRadius: '6px',
                                  width: '32px',
                                  height: '32px',
                                  cursor: submitting || loading ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  opacity: submitting || loading ? 0.6 : 1
                                }}
                                title="Edit Note"
                              >
                                <EditIcon sx={{ fontSize: '16px' }} />
                              </button>

                              <button
                                onClick={() => deleteNote(note.id)}
                                disabled={submitting || loading}
                                style={{
                                  backgroundColor: '#fff',
                                  color: '#000',
                                  border: 'none',
                                  borderRadius: '6px',
                                  width: '32px',
                                  height: '32px',
                                  cursor: submitting || loading ? 'not-allowed' : 'pointer',
                                  fontSize: '14px',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  transition: 'all 0.2s ease',
                                  opacity: submitting || loading ? 0.6 : 1
                                }}
                                title="Delete Note"
                              >
                                <DeleteIcon sx={{ fontSize: '16px' }} />
                              </button>
                            </div>
                          </div>

                          {/* Main Content */}
                          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <h3 style={{
                              fontSize: isGridView ? '20px' : '28px',
                              fontWeight: 'bold',
                              lineHeight: 1.3,
                              color: textColors.primary,
                              textShadow: isLightColor(note.color) ? 'none' : `0 1px 2px ${textColors.shadow}`,
                              fontFamily: '"Saira", sans-serif',
                              margin: 0
                            }}>
                              {isGridView
                                ? truncateText(note.name || 'Untitled Note', 50)
                                : (note.name || 'Untitled Note')
                              }
                            </h3>

                            <p style={{
                              fontSize: isGridView ? '14px' : '16px',
                              color: textColors.secondary,
                              textShadow: isLightColor(note.color) ? 'none' : `0 1px 1px ${textColors.shadow}`,
                              lineHeight: 1.6,
                              fontFamily: '"Saira", sans-serif',
                              margin: 0
                            }}>
                              {isGridView
                                ? truncateText(note.description || 'No description', 100)
                                : (note.description || 'No description')
                              }
                            </p>
                          </div>

                          {/* Footer */}
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingTop: '12px',
                            borderTop: `1px solid ${textColors.border}`
                          }}>
                            <span style={{
                              fontSize: isGridView ? '12px' : '14px',
                              fontWeight: 'medium',
                              color: textColors.muted,
                              fontFamily: '"Saira", sans-serif'
                            }}>
                              {isGridView
                                ? new Date(note.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                                : `Created: ${new Date(note.created_at).toLocaleDateString()}`
                              }
                            </span>
                            {!isGridView && note.updated_at !== note.created_at && (
                              <span style={{
                                fontSize: '12px',
                                color: textColors.muted,
                                fontFamily: '"Saira", sans-serif'
                              }}>
                                Updated: {new Date(note.updated_at).toLocaleDateString()}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{
                      gridColumn: '1 / -1',
                      padding: '64px 32px',
                      textAlign: 'center',
                      backgroundColor: '#f5f5f5',
                      borderRadius: '12px',
                      border: '1px solid #e0e0e0'
                    }}>
                      <div style={{ fontSize: '64px', color: '#ccc', marginBottom: '24px' }}>
                        {searchQuery || categoryFilter !== 'all' ? '🔍' : 'ℹ️'}
                      </div>
                      <h4 style={{ 
                        fontSize: '32px', 
                        fontWeight: 'bold', 
                        color: '#333', 
                        marginBottom: '16px',
                        fontFamily: '"Saira", sans-serif'
                      }}>
                        {searchQuery || categoryFilter !== 'all' ? 'No matching notes found' : 'No notes found'}
                      </h4>
                      <p style={{ 
                        fontSize: '16px', 
                        color: '#666', 
                        marginBottom: '32px', 
                        maxWidth: '400px', 
                        margin: '0 auto 32px auto',
                        fontFamily: '"Saira", sans-serif'
                      }}>
                        {searchQuery || categoryFilter !== 'all' ?
                          'No notes match your current search criteria. Try adjusting your search or filters, or create a new note.' :
                          'You haven\'t created any notes yet. Get started by creating your first note.'
                        }
                      </p>
                      <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
                        <button
                          onClick={handleAddNew}
                          disabled={submitting}
                          style={{
                            backgroundColor: '#ce55b9',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '12px 24px',
                            cursor: submitting ? 'not-allowed' : 'pointer',
                            fontSize: '16px',
                            fontFamily: '"Saira", sans-serif',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: submitting ? 0.6 : 1
                          }}
                        >
                          ➕ {submitting ? 'Processing...' : 'Create New Sticky Note'}
                        </button>
                        {(searchQuery || categoryFilter !== 'all') && (
                          <button
                            onClick={() => {
                              setSearchQuery('');
                              setCategoryFilter('all');
                              setCurrentPage(1);
                            }}
                            style={{
                              backgroundColor: 'transparent',
                              color: '#666',
                              border: '1px solid #ccc',
                              borderRadius: '8px',
                              padding: '12px 24px',
                              cursor: 'pointer',
                              fontSize: '16px',
                              fontFamily: '"Saira", sans-serif',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}
                          >
                            🔄 Clear All Filters
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="row mt-4">
                <div className="col-12 d-flex justify-content-center">
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                          color: currentPage === 1 ? '#ccc' : '#333',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                          fontFamily: '"Saira", sans-serif'
                        }}
                      >
                        Previous
                      </button>
                      
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <button
                          key={page}
                          onClick={() => handlePageChange(page)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: currentPage === page ? '#ce55b9' : '#fff',
                            color: currentPage === page ? 'white' : '#333',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontFamily: '"Saira", sans-serif',
                            fontWeight: currentPage === page ? 'bold' : 'normal'
                          }}
                        >
                          {page}
                        </button>
                      ))}
                      
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        style={{
                          padding: '8px 16px',
                          backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                          color: currentPage === totalPages ? '#ccc' : '#333',
                          border: '1px solid #ddd',
                          borderRadius: '6px',
                          cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                          fontFamily: '"Saira", sans-serif'
                        }}
                      >
                        Next
                      </button>
                    </div>
                    <div style={{ 
                      fontSize: '14px', 
                      color: '#666', 
                      textAlign: 'center',
                      fontFamily: '"Saira", sans-serif'
                    }}>
                      Page {currentPage} of {totalPages} ({totalItems} total items)
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {open && (
        <div style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '450px',
          maxWidth: '90vw',
          backgroundColor: 'white',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.1)',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          fontFamily: '"Saira", sans-serif'
        }}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{
              background: '#ce55b9',
              color: 'white',
              padding: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <h5 style={{ 
                  fontSize: '20px', 
                  fontWeight: 600, 
                  fontFamily: '"Saira", sans-serif',
                  margin: 0
                }}>
                  {editMode ? 'Edit Note' : 'Add New Note'}
                </h5>
              
              </div>
              <button
                onClick={closeSlideWindow}
                disabled={submitting}
                style={{
                  color: 'white',
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  border: 'none',
                  borderRadius: '6px',
                  width: '32px',
                  height: '32px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div style={{
              flex: 1,
              padding: '24px',
              overflow: 'auto',
              background: '#ffffff'
            }}>
             
              {/* Title Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Note Title *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  onBlur={() => markFieldAsTouched('name')}
                  disabled={submitting}
                  placeholder="What is this note about?"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: getFieldError('name') ? '2px solid #f44336' : 
                           isFieldValid('name') ? '2px solid #4caf50' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: getFieldError('name') ? 'rgba(244, 67, 54, 0.05)' :
                                   isFieldValid('name') ? 'rgba(76, 175, 80, 0.05)' : 'white',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: getFieldError('name') ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {getFieldError('name') || 'Enter a descriptive title for your note (3-200 characters)'}
                </div>
              </div>

              {/* Description Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Description *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  onBlur={() => markFieldAsTouched('description')}
                  disabled={submitting}
                  placeholder="Write your note content here..."
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: getFieldError('description') ? '2px solid #f44336' : 
                           isFieldValid('description') ? '2px solid #4caf50' : '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontFamily: '"Saira", sans-serif',
                    backgroundColor: getFieldError('description') ? 'rgba(244, 67, 54, 0.05)' :
                                   isFieldValid('description') ? 'rgba(76, 175, 80, 0.05)' : 'white',
                    outline: 'none',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <div style={{
                  marginTop: '4px',
                  fontSize: '12px',
                  color: getFieldError('description') ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {getFieldError('description') || 'Write your note content (3-1000 characters)'}
                </div>
              </div>

              {/* Category Field */}
              <div style={{ marginBottom: '24px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 600,
                  color: '#333',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  Category *
                </label>
                <FormControl 
  fullWidth 
  error={!!getFieldError('category')}
  sx={{
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      fontFamily: '"Saira", sans-serif',
      '&.Mui-error': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#f44336',
        }
      },
      '&.Mui-focused': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: isFieldValid('category') ? '#4caf50' : '#ce55b9',
        }
      }
    },
    '& .MuiInputLabel-root': {
      fontFamily: '"Saira", sans-serif',
      '&.Mui-focused': {
        color: isFieldValid('category') ? '#4caf50' : '#ce55b9'
      }
    }
  }}
>
  <InputLabel sx={{ fontFamily: '"Saira", sans-serif' }}>Category</InputLabel>
  <Select
    value={formData.category}
    label="Category"
    onChange={(e) => handleInputChange('category', e.target.value)}
    onBlur={() => markFieldAsTouched('category')}
    disabled={submitting}
    sx={{
      fontFamily: '"Saira", sans-serif',
      '& .MuiSelect-select': {
        fontFamily: '"Saira", sans-serif'
      }
    }}
  >
    {categoryOptions.map((category) => (
      <MenuItem
        key={category.id}
        value={category.id}
        sx={{ fontFamily: '"Saira", sans-serif' }}
      >
        {category.name}
      </MenuItem>
    ))}
  </Select>
  {getFieldError('category') && (
    <FormHelperText sx={{ fontFamily: '"Saira", sans-serif' }}>
      {getFieldError('category')}
    </FormHelperText>
  )}
</FormControl>
                
              </div>

              <div style={{
                height: '1px',
                backgroundColor: '#ddd',
                margin: '24px 0'
              }} />

              {/* UPDATED: Color Picker Section with Validation */}
              <div style={{
                padding: '24px',
                backgroundColor: getFieldError('color') ? 'rgba(244, 67, 54, 0.05)' : 'rgba(206, 85, 185, 0.05)',
                borderRadius: '8px',
                border: getFieldError('color') ? '1px solid #f44336' : '1px solid rgba(206, 85, 185, 0.2)'
              }}>
                <div style={{
                  marginBottom: '16px',
                  fontWeight: 600,
                  color: getFieldError('color') ? '#f44336' : '#ce55b9',
                  fontFamily: '"Saira", sans-serif',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px'
                }}>
                  🎨 Note Color *
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    border: getFieldError('color') ? '2px solid #f44336' : '2px solid #ddd',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    backgroundColor: formData.color || '#f5f5f5',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '12px',
                    color: '#999'
                  }}>
                    {!formData.color && '?'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <span style={{ fontSize: '14px', fontFamily: '"Saira", sans-serif' }}>
                      {formData.color ? getColorName(formData.color) : 'No color selected'}
                    </span>
                    <span style={{ fontSize: '12px', color: '#666', fontFamily: '"Saira", sans-serif' }}>
                      {formData.color ? `Contrast: ${validateContrast(formData.color, getTextColors(formData.color).primary).level}` : 'Please select a color'}
                    </span>
                  </div>
                </div>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
                  {colorOptions.map((color) => (
                    <button
                      key={color.value}
                      onClick={() => handleColorSelect(color.value)}
                      onBlur={() => markFieldAsTouched('color')}
                      disabled={submitting}
                      style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: color.value,
                        border: formData.color === color.value ? '2px solid #ce55b9' : '1px solid #ddd',
                        borderRadius: '6px',
                        cursor: submitting ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s ease',
                        opacity: submitting ? 0.6 : 1
                      }}
                      title={color.label}
                    />
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <input
                    type="color"
                    value={formData.color || '#000000'}
                    onChange={handleCustomColorChange}
                    onBlur={() => markFieldAsTouched('color')}
                    disabled={submitting}
                    style={{
                      width: '32px',
                      height: '32px',
                      border: getFieldError('color') ? '2px solid #f44336' : 'none',
                      borderRadius: '6px',
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting ? 0.6 : 1
                    }}
                  />
                  <input
                    type="text"
                    value={formData.color}
                    onChange={(e) => handleInputChange('color', e.target.value)}
                    onBlur={() => markFieldAsTouched('color')}
                    placeholder="#000000"
                    disabled={submitting}
                    style={{
                      flex: 1,
                      padding: '8px 12px',
                      border: getFieldError('color') ? '2px solid #f44336' : 
                             isFieldValid('color') ? '2px solid #4caf50' : '1px solid #ddd',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontFamily: '"Saira", sans-serif',
                      backgroundColor: getFieldError('color') ? 'rgba(244, 67, 54, 0.05)' :
                                     isFieldValid('color') ? 'rgba(76, 175, 80, 0.05)' : 'white',
                      outline: 'none',
                      opacity: submitting ? 0.6 : 1
                    }}
                  />
                </div>
                
                {/* UPDATED: Add validation error display for color */}
                <div style={{
                  fontSize: '12px',
                  color: getFieldError('color') ? '#f44336' : '#666',
                  fontFamily: '"Saira", sans-serif'
                }}>
                  {getFieldError('color') || 'Choose a color for your note or enter a custom hex color'}
                </div>
              </div>
            </div>

            {/* Footer Actions */}
            <div style={{
              padding: '24px',
              borderTop: '1px solid rgba(0,0,0,0.1)',
              background: 'white',
              display: 'flex',
              gap: '16px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={closeSlideWindow}
                disabled={submitting}
                style={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '100px',
                  backgroundColor: 'transparent',
                  color: '#666',
                  border: '1px solid #ddd',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  opacity: submitting ? 0.6 : 1
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || (!isFormValid && Object.keys(validationTouched).length > 0)}
                style={{
                  background: isFormValid ? '#ce55b9' : '#ce55b9',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: '"Saira", sans-serif',
                  padding: '12px 24px',
                  minWidth: '140px',
                  color: 'white',
                  border: 'none',
                  cursor: submitting || (!isFormValid && Object.keys(validationTouched).length > 0) ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  opacity: submitting || (!isFormValid && Object.keys(validationTouched).length > 0) ? 0.6 : 1
                }}
              >
                {submitting ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid transparent',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }} />
                    <span style={{ fontFamily: '"Saira", sans-serif' }}>
                      {editMode ? 'Updating...' : 'Creating...'}
                    </span>
                  </>
                ) : (
                  <>
                    {isFormValid && <span style={{ fontSize: '16px' }}></span>}
                    <span style={{ fontFamily: '"Saira", sans-serif' }}>
                      {editMode ? 'Update Note' : 'Add Note'}
                    </span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Overlay */}
      {open && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1200
          }}
          onClick={closeSlideWindow}
        />
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
};

export default NotesListTab;